import Css from "./style.module.scss";

import * as Icons from "@phosphor-icons/react";
import { Pagination } from "nlib/ui";
import { getMatchesData, getSelectedBusinessId } from "selectors/businesses";
import { getTextsData } from "selectors/texts";
import { useDispatch, useSelector } from "react-redux";
import { useMainApi } from "hooks";
import BusinessesActions from "actions/BusinessesActions";
import Constants from "const/Constants";
import DataStatuses from "const/DataStatuses";
import DocumentPaymentTypes from "const/DocumentPaymentTypes";
import Filters from "./lib/Filters";
import MainApiRoutes from "const/MainApiRoutes";
import Modal from "nlib/ui/Modal";
import NoDataContent from "nlib/common/NoDataContent";
import Preloader from "nlib/common/Preloader";
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState } from "react";
import RestApi from "api/RestApi";
import Table, { TableHead, TableRow } from "nlib/ui/Table";
import TransactionTypes from "const/TransactionTypes";
import TransactionsTableRow from "./lib/TransactionsTableRow";

const { BUSINESSES, TRANSACTIONS, STATS } = MainApiRoutes;

const STATUSES = [DataStatuses.TO_RECONCILE, DataStatuses.NEED_DOCUMENT, DataStatuses.NEED_REACTION, DataStatuses.TO_REVIEW];

const PairTransactionsWindow = ({ documentData, onClose, ...restProps }) => {
  const {
    paid,
    paymentType,
    id: documentId,
    paidTransactions = [],
    status: documentStatus
  } = documentData;

  const dispatch = useDispatch();

  const { uiTexts } = useSelector(getTextsData);

  const matchesData = useSelector(getMatchesData);

  const selectedBusinessId = useSelector(getSelectedBusinessId);

  const [matchingFetching, setMatchingFetching] = useState(false);

  const [modalInited, setModalInited] = useState(false);

  const [filters, setFilters] = useState({});

  const transactionType = useMemo(() => {
    if (paymentType === DocumentPaymentTypes.BUY) return TransactionTypes.WITHDRAW;
    if (paymentType === DocumentPaymentTypes.SELL) return TransactionTypes.DEPOSIT;

    return null;
  }, [paymentType]);

  const [sortingProps, setSortingProps] = useState({});

  const [page, setPage] = useState(1);

  const [{ results: transactionsData }, fetchingTransactions] = useMainApi({
    method: RestApi.REQUEST_METHODS.POST,
    initialData: { results: null },
    fetchCondition: (
      !filters.text || filters.text.length >= Constants.SEARCH_TEXT_MIN_LENGTH
    ),
    parameters: [
      `${BUSINESSES}/${selectedBusinessId}${TRANSACTIONS}`,
      null,
      {
        ...filters,
        text: filters.text || null,
        type: transactionType,
        status: STATUSES,
        offset: (page - 1) * Constants.TABLE_PAGE_SIZE,
        limit: Constants.TABLE_PAGE_SIZE,
        sortings: sortingProps.id ? sortingProps : null,
        pairing: true
      }
    ],
    dependencies: [filters, page, sortingProps, transactionType]
  });

  const [transactionsStats, fetchingTransactionsStats] = useMainApi({
    method: RestApi.REQUEST_METHODS.GET,
    initialData: {},
    fetchCondition: (
      !filters.text || filters.text.length >= Constants.SEARCH_TEXT_MIN_LENGTH
    ),
    parameters: [
      `${BUSINESSES}/${selectedBusinessId}${TRANSACTIONS}${STATS}`,
      {
        ...filters,
        text: filters.text || null,
        type: transactionType,
        pairing: true
      }
    ],
    dependencies: [filters, transactionType]
  });

  const matchingTransactions = useMemo(() => {
    return matchesData.map(({ transaction }) => transaction)
      .filter(({ id }) => paidTransactions.every((transaction) => transaction.id !== id));
  }, [matchesData, paidTransactions]);

  const allTransactions = useMemo(() => {
    return transactionsData
      ? transactionsData.filter(({ id }) => paidTransactions.every((transaction) => transaction.id !== id)) : [];
  }, [paidTransactions, transactionsData]);

  const fetchMatching = useCallback(async() => {
    setMatchingFetching(true);
    await dispatch(BusinessesActions.fetchDataMatchesList(null, documentId, true));
    setMatchingFetching(false);
  }, [dispatch, documentId]);

  const handleChangeFilters = useCallback((value) => {
    setFilters((prev) => ({ ...prev, ...value }));
    setPage(1);
  }, []);

  const handleTableSortChange = useCallback((value) => {
    setSortingProps({ id: value.sortBy, desc: value.sortOrder === "desc" });
  }, []);

  const handlePageChange = useCallback((value) => {
    setPage(value);
  }, []);

  useLayoutEffect(() => {
    fetchMatching();
  }, [fetchMatching]);

  const fetchingData = fetchingTransactions || fetchingTransactionsStats || matchingFetching;

  useEffect(() => {
    if (!fetchingData) setModalInited(true);
  }, [fetchingData]);

  return (
    <Modal
      {...restProps}
      iconComponent={Icons.LinkSimpleHorizontal}
      title={uiTexts.matchTransactions}
      className={Css.pairTransactionsWindow}
      dialogClassName={Css.modalDialog}
      onClose={onClose}>
      <div className={Css.contentWrap}>
        <Filters
          disabled={fetchingData}
          filters={filters}
          className={!modalInited && Css.hidden}
          onChange={handleChangeFilters} />
        {(allTransactions.length || matchingTransactions.length || paidTransactions.length)
          ? (
            <>
              <Table
                theme="flat"
                sortBy={sortingProps.id}
                sortOrder={sortingProps.desc ? "desc" : "asc"}
                onSortChange={handleTableSortChange}
                className={Css.table}>
                <TableRow>
                  <TableHead className={Css.statusCell}>{uiTexts.status}</TableHead>
                  <TableHead className={Css.accountCell}>{uiTexts.account}</TableHead>
                  <TableHead className={Css.amountCell}>{uiTexts.total}</TableHead>
                  <TableHead className={Css.addressCell}>{uiTexts.payee}</TableHead>
                  <TableHead className={Css.timestampCell}>{uiTexts.date}</TableHead>
                  <TableHead className={Css.descriptionCell}>{uiTexts.description}</TableHead>
                  <TableHead className={Css.actionsCell}>{uiTexts.actions}</TableHead>
                </TableRow>
                {!!paidTransactions.length && (
                  <>
                    <div className={Css.title}>{uiTexts.pairedTransactions}</div>
                    {paidTransactions.map((transaction) => (
                      <TransactionsTableRow
                        paired
                        key={transaction.id}
                        transaction={transaction}
                        disabled={documentStatus !== DataStatuses.TO_REVIEW && (paid || paidTransactions.length)}
                        documentId={documentId}
                        onClose={onClose} />
                    ))}
                  </>
                )}
                {!!matchingTransactions.length && (
                  <>
                    <div className={Css.title}>{uiTexts.matchingTransactions}</div>
                    {matchingTransactions.map((transaction) => (
                      <TransactionsTableRow
                        key={transaction.id}
                        documentId={documentId}
                        transaction={transaction}
                        disabled={documentStatus !== DataStatuses.TO_REVIEW && (paid || paidTransactions.length)}
                        onClose={onClose} />
                    ))}
                  </>
                )}
                {allTransactions.length ? (
                  <>
                    {(!!matchingTransactions.length || !!paidTransactions.length) && (
                      <div className={Css.title}>{uiTexts.allTransactions}</div>
                    )}
                    {allTransactions.map((transaction) => (
                      <TransactionsTableRow
                        key={transaction.id}
                        transaction={transaction}
                        disabled={documentStatus !== DataStatuses.TO_REVIEW && (paid || paidTransactions.length)}
                        documentId={documentId}
                        onClose={onClose} />
                    ))}
                  </>
                ) : (fetchingData ? <Preloader className={Css.preloader} /> : (
                  <NoDataContent
                    icon={Icons.CreditCard}
                    title={uiTexts.noTransactionsFound} />
                ))}
              </Table>
              {(transactionsStats?.total?.count > Constants.TABLE_PAGE_SIZE) && (
                <Pagination
                  className={Css.pagination}
                  count={transactionsStats?.total?.count || 0}
                  page={page}
                  pageSize={Constants.TABLE_PAGE_SIZE}
                  disabled={fetchingData}
                  onChange={handlePageChange} />
              )}
            </>
          )
          : (fetchingData
            ? <Preloader />
            : (
              <NoDataContent
                icon={Icons.CreditCard}
                title={uiTexts.noTransactionsFound} />
            ))}
      </div>
    </Modal>
  );
};

export default React.memo(PairTransactionsWindow);
